/**
 * Theme registry.
 *
 * The ids here must stay in sync with two other places:
 *   - the `[data-theme="..."]` selectors in themes.css
 *   - VALID_THEMES in backend/api/routers/preferences.py, which rejects
 *     anything else on save
 */

export const THEME_IDS = ["torrens-dark", "torrens-light", "classic-light"] as const;

export type ThemeId = (typeof THEME_IDS)[number];

/** Applied when a user has saved no preference. */
export const DEFAULT_THEME: ThemeId = "torrens-dark";

export interface ThemeMeta {
  id: ThemeId;
  label: string;
  description: string;
  /** Drives `color-scheme` and the swatch preview. */
  isDark: boolean;
  /** Representative colours for the settings preview, in [surface, raised, accent]. */
  swatch: [string, string, string];
}

export const THEMES: ThemeMeta[] = [
  {
    id: "torrens-dark",
    label: "Torrens Dark",
    description: "Torrens University black and orange. The default theme.",
    isDark: true,
    swatch: ["#0A0A0B", "#141416", "#FF5000"],
  },
  {
    id: "torrens-light",
    label: "Torrens Light",
    description: "Torrens brand colours on white — better for bright rooms and printing.",
    isDark: false,
    swatch: ["#FFFFFF", "#F7F7F8", "#FF5000"],
  },
  {
    id: "classic-light",
    label: "Classic Light",
    description: "The original blue and grey appearance.",
    isDark: false,
    swatch: ["#F9FAFB", "#FFFFFF", "#2563EB"],
  },
];

export function isThemeId(value: unknown): value is ThemeId {
  return typeof value === "string" && (THEME_IDS as readonly string[]).includes(value);
}

export function themeMeta(id: ThemeId): ThemeMeta {
  return THEMES.find((t) => t.id === id) ?? THEMES[0];
}

/**
 * Set the theme on the document root.
 *
 * Also updates the `theme-color` meta tag so the browser chrome on mobile and
 * the address bar match, which is otherwise a jarring white strip above a
 * near-black page.
 */
export function applyTheme(id: ThemeId): void {
  const root = document.documentElement;
  root.setAttribute("data-theme", id);

  const meta = themeMeta(id);
  let themeColorTag = document.querySelector('meta[name="theme-color"]');
  if (!themeColorTag) {
    themeColorTag = document.createElement("meta");
    themeColorTag.setAttribute("name", "theme-color");
    document.head.appendChild(themeColorTag);
  }
  themeColorTag.setAttribute("content", meta.swatch[0]);
}
